import { service } from '@ember/service'
import Component from '@glimmer/component'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import { t, type IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import type { SeriesBarOptions } from 'highcharts'
import { colors } from '@colors'
import type { StoriesWrittenByGradeData } from 'teacher-ui-client/utils/student-events/fetchers/stories-written-by-grade'

interface Args {
  data: StoriesWrittenByGradeData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardStoriesWritten extends Component<Signature> {
  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  readonly singleGradeChartMargin: (number | null)[] = [55, null, 104, null]

  /**
   * The series labels for the chart, formatted for Highcharts.
   * A list of formatted grade names.
   */
  get categories(): string[] {
    return this.args.data.stories.map((grade) => this.gradeSets.findByPosition(grade.grade_position)?.name ?? '')
  }

  /**
   * Bar data formatted for Highcharts
   */
  get barData(): SeriesBarOptions[] {
    const { stories } = this.args.data
    const data = stories.map((grade) => {
      if (grade.student_count === 0) {
        return 0
      }
      return Math.round(grade.story_count / grade.student_count)
    })

    return [
      {
        data,
        color: colors.oceanyBlue300,
        type: 'bar',
      },
    ]
  }

  get subtitleKey(): string {
    if (this.singleGrade) return 'reporting.dashboardSubtitles.singleGrade.storiesWritten'
    return 'reporting.dashboardSubtitles.multiGrade'
  }

  /**
   * Total number of students across all grades
   * Calculates number of contributing students
   */
  get studentCount(): string {
    const count = this.args.data.student_count ?? 0
    return formatNumber(count, 2)
  }

  /**
   * Flag if data has only one grade
   */
  get singleGrade(): boolean {
    return this.args.data.stories.length === 1
  }

  get xAxisLabel(): string {
    return this.intl.t('grade.label')
  }

  get yAxisLabel(): string {
    return this.singleGrade
      ? this.intl.t('reporting.charts.dashboard.storiesWritten.numberOfStories')
      : this.intl.t('reporting.charts.dashboard.storiesWritten.storiesWritten')
  }

  <template>
    <div data-test-giraffe-dashboard-stories-written class="flex h-full flex-col" ...attributes>
      <div class="flex justify-between">
        <p data-test-dashboard-subtitle class="text-dusty-black-500 mb-3 pr-[5px] pl-0 text-xs">
          {{t this.subtitleKey}}
        </p>
        <p class="text-dusty-black-500 mb-3 pr-0 pl-[5px] text-right text-xs">
          {{t "reporting.dashboardSubtitles.studentCount" count=this.studentCount}}
        </p>
      </div>
      <div class="relative grow">
        <div class="absolute inset-0">
          {{#if this.singleGrade}}
            <SeriesBar
              @data={{this.barData}}
              @categories={{this.categories}}
              @xAxisLabel={{this.xAxisLabel}}
              @yAxisLabel={{this.yAxisLabel}}
              @groupPadding={{0}}
              @chartMargin={{this.singleGradeChartMargin}}
              @yAxisOffset={{25}}
              @yAxisLineWidth={{1}}
              @yAxisGridLineWidth={{0}}
            />
          {{else}}
            <SeriesBar
              @data={{this.barData}}
              @categories={{this.categories}}
              @xAxisLabel={{this.xAxisLabel}}
              @yAxisLabel={{this.yAxisLabel}}
            />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}

export default DashboardStoriesWritten
